/**
 * Rinnovo silenzioso della sessione dell'associato sul sito («rinnovo a 90», decisione del
 * committente del 25/10/2026): chi apre il sito almeno una volta ogni 90 giorni non rifà mai
 * l'accesso. Regole pure (niente axios, niente localStorage): le usa
 * `services/rinnovoSessioneAssociato.ts`, e `isAuthenticated()` per non buttare un token
 * scaduto ma ancora rinnovabile.
 *
 * Specchio di `backend/utils/rinnovoSito.js`: rinnovabile dopo la scadenza solo il token del
 * sito di titolare o dipendente, emesso (claim `iat`) da meno di 90 giorni e lungo almeno 24 ore
 * (`exp - iat`: il sito dura 7 giorni, l'app e l'impersonificazione 1 ora); mai
 * l'impersonificazione (`impersonatedBy`) né l'access token dell'app (`canale`).
 */

export const FINESTRA_RINNOVO_MS = 90 * 24 * 60 * 60 * 1000;
/** Ogni quanto si rinnova con la pagina aperta. */
export const INTERVALLO_RINNOVO_MS = 12 * 60 * 60 * 1000;
/** All'apertura del sito non si rinnova un token emesso da meno di così (appena fatto l'accesso). */
export const ETA_MINIMA_RINNOVO_MS = 60 * 60 * 1000;
const TOLLERANZA_OROLOGIO_MS = 60 * 1000;
/** Durata minima (exp - iat) di un token del sito, in secondi. */
const DURATA_MINIMA_TOKEN_SITO_S = 24 * 60 * 60;

export interface PayloadToken {
  exp?: number;
  iat?: number;
  type?: string;
  impersonatedBy?: string;
  canale?: string;
}

/** Claim del JWT (senza verificarne la firma: lo fa il server). null se illeggibile. */
export function leggiPayload(token: string | null | undefined): PayloadToken | null {
  if (!token || typeof token !== 'string') return null;
  const parti = token.split('.');
  if (parti.length !== 3) return null;
  try {
    const b64 = parti[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as PayloadToken) : null;
  } catch (e) {
    return null;
  }
}

/** Token del sito che il server sa rinnovare (titolare o dipendente, mai impersonificazione o app). */
export function tokenDelSitoRinnovabile(p: PayloadToken | null): boolean {
  if (!p || p.canale) return false;
  return (p.type === 'associate' && !p.impersonatedBy) || p.type === 'dipendente';
}

export type StatoToken = 'valido' | 'rinnovabile' | 'scaduto';

/**
 * - 'valido'      → non scaduto (o senza `exp`);
 * - 'rinnovabile' → scaduto, ma il server lo rinnova ancora (entro 90 giorni da `iat`);
 * - 'scaduto'     → scaduto e non rinnovabile: va fatto l'accesso.
 */
export function statoToken(p: PayloadToken | null, adesso: number = Date.now()): StatoToken {
  if (!p) return 'scaduto';
  if (typeof p.exp !== 'number' || p.exp * 1000 > adesso) return 'valido';
  if (!tokenDelSitoRinnovabile(p) || typeof p.iat !== 'number') return 'scaduto';
  if (p.exp - p.iat < DURATA_MINIMA_TOKEN_SITO_S) return 'scaduto';
  const emesso = p.iat * 1000;
  if (emesso > adesso + TOLLERANZA_OROLOGIO_MS) return 'scaduto';
  return adesso - emesso < FINESTRA_RINNOVO_MS ? 'rinnovabile' : 'scaduto';
}

/**
 * Vero se conviene chiedere il rinnovo: token del sito rinnovabile dal server e scaduto ma
 * ancora nella finestra, oppure valido ed emesso da almeno `etaMinimaMs`.
 * Senza `iat` (token anomalo) si rinnova solo se valido.
 */
export function daRinnovare(p: PayloadToken | null, adesso: number = Date.now(), etaMinimaMs: number = ETA_MINIMA_RINNOVO_MS): boolean {
  if (!tokenDelSitoRinnovabile(p)) return false;
  const stato = statoToken(p, adesso);
  if (stato === 'rinnovabile') return true;
  if (stato !== 'valido') return false;
  if (typeof p!.iat !== 'number') return true;
  return adesso - p!.iat * 1000 >= etaMinimaMs;
}
